// Panel de gestión del detalle de una observación del hilo (README §3,
// aside de 320 px) — postventa y, desde ADR-0134, facility. Tres filas de
// lectura —Hito, Ciclo, quien la tiene (Contratista / Proveedor)— y un
// cuerpo que depende del estado:
//
// | kind               | cuerpo                                               |
// |--------------------|------------------------------------------------------|
// | open               | Aceptar (sin mensaje) · Rechazar/Anular              |
// |   ↳ rechazando     | motivo obligatorio + Confirmar · Cancelar            |
// | reopened           | cuerpo de asignar + Reasignar · Rechazar/Anular      |
// |   ↳ rechazando     | motivo obligatorio + Confirmar · Cancelar            |
// | accepted           | cuerpo de asignar (+ Anular si el grafo lo da)       |
// | assigned           | aviso + Reasignar (+ Anular si el grafo lo da)       |
// |   ↳ visita (F8)    | «Agendar visita» / «Cambiar visita» (día + horario) |
// |   ↳ reasignando    | el mismo cuerpo de accepted (estado UI local)        |
// | resolved           | veredicto si el carril cierra; si no, decide otro    |
// | rejected (espera)  | espera la respuesta del propietario (ADR-0141)       |
// | rejected (terminal)| la anulada de facility: cerrada, como el archivo     |
// | appealed           | acoger apelación · mantener rechazo (motivo oblig.)  |
// | conforme/rejected_closed | cerrada, queda en el archivo                   |
//
// Los botones salen de `resolveGestionActions` / `resolveVerdictActions`
// (grafo y cierre de la receta para el carril del usuario), jamás de un
// mapa estado→botones: sin destino no hay botón. «Reasignar» NO inventa la
// arista asignada → aceptada (no existe): es la autoarista asignada →
// asignada que `/assign` resuelve en servidor.
//
// Dos sabores de asignar (`assignFlavor`): con taxonomía (postventa) la
// asignación clasifica en línea (AsignarForm); sin ella (facility) es el
// flujo de #0431 — elegir o dar de alta al proveedor y confirmar el
// destinatario (ProviderAssignForm).
//
// El veredicto (Conforme / No conforme) es de los carriles de cierre de la
// receta. Postventa cierra sólo con el propietario (desde la PWA), así que
// su panel sigue sin botones en «resuelta». Facility cierra también con el
// supervisor / gerente: Conforme estampa el cierre; No conforme exige
// motivo y reabre con un ciclo nuevo. Las fotos de la No conformidad son
// opcionales para ese carril (ADR-0134 §5) y el dashboard no sube fotos de
// hallazgos, así que va sólo con el motivo; un carril que debe la
// constancia completa (foto + voz) no ve el botón y la da desde la PWA.
//
// «Corregir clasificación» va deshabilitado y sólo con taxonomía (sin
// pantalla todavía). «Reenviar enlace» no se muestra: no hay endpoint y
// volver a llamar a /assign revocaría el enlace vigente (fundador
// 2026-10-03: ocultar lo que no tiene uso en vez de deshabilitarlo).
//
// El enlace recién emitido vive en el estado del PANEL, fuera del cuerpo
// por fase: tras `/assign` el refetch cambia la fase y desmonta el cuerpo
// de asignar, y sin notificaciones propias ese enlace es el canal.

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  cierrePrevistoPersonal,
  fechaApoyoPropietario,
  VEREDICTO_LABEL,
  VISIT_PREFERENCE_MISMATCH_HINT,
  VISIT_SAME_AS_DECLINED_MESSAGE,
  visitaLabel,
  visitContactCoordinationLine,
  visitReasonLabelForAsset,
  type FindingDetailResponse,
  type UnitVisitContact,
} from "@bitacorapro/api-types";
import { CopyLinkField } from "./CopyLinkField";
import { ProviderAssignForm } from "./ProviderAssignForm";
import { ProviderListBoundary } from "./skeleton-compositions/ProviderListSkel";
import {
  faltaDeVisita,
  visitaBody,
  VisitaFields,
  visitaErrorMessage,
  VISITA_VACIA,
  type VisitaDraft,
} from "./VisitaFields";
import { useConformity, useScheduleVisit, useUpdateObservationStatus } from "../lib/api/queries";
import {
  rejectWords,
  type AppealActions,
  type AssignFlavor,
  type GestionActions,
  type GestionTarget,
  type VerdictActions,
} from "../lib/postventa/actions";
import type { PresentationalState } from "../lib/postventa/phase";
import { useAssignFlow } from "../lib/postventa/useAssignFlow";
import { visitaPropuestaRows } from "../lib/postventa/visitaPropuesta";
import { visitChanged, visitMatchesPreference } from "@bitacorapro/core-engine";
import { capitalize, type ThreadWords } from "../lib/postventa/words";
import { useToast } from "../lib/useToast";

type Obs = FindingDetailResponse["observation"];

/** F8 (corte 6) — lo que el panel necesita para la visita: si la receta
 *  agenda (contraparte externa), si ESTE carril puede agendar ahora
 *  (`visitSchedulable` del detalle) y el pedido de nueva visita del
 *  contratista que todavía nadie atendió. */
export type VisitaPanel = {
  enabled: boolean;
  schedulable: boolean;
  request: FindingDetailResponse["visitRequest"] | null;
  /** ADR-0150 — el contacto para visitas vigente de la unidad (la
   *  arrendataria): «Arrendada · coordinar con {nombre} {teléfono}». */
  contact?: UnitVisitContact | null;
  /** ADR-0160 — la visita vigente la propuso el contratista desde su
   *  enlace (por confirmar), y las fechas del ciclo. */
  proposal?: FindingDetailResponse["visitProposal"] | null;
  history?: FindingDetailResponse["visitHistory"];
};

const SIN_VISITA: VisitaPanel = { enabled: false, schedulable: false, request: null };

/** Lo que el panel necesita de la receta, ya resuelto por la pantalla. */
export type GestionContext = {
  words: ThreadWords;
  flavor: AssignFlavor;
  verdict: VerdictActions;
  /** Etiqueta del estado de entrada («Solicitada», «Abierta») — adonde
   *  vuelve una No conformidad. */
  entryLabel: string;
  /** Etiqueta del estado resuelto («Lista para revisar», «Resuelta»). */
  resolvedLabel: string;
  /** Sólo el asignado llega a resuelta (postventa): el panel puede decir
   *  «el contratista marcó…». Si también resuelve otro carril, no. */
  onlyAssigneeResolves: boolean;
  /** El objeto, para la nota prellenada de la asignación («Tienda 34»). */
  assetTitle: string | null;
  /** « del proyecto» cuando la vertical archiva por proyecto; si no, "". */
  archivoDe: string;
};

/** `note` de /assign admite hasta 500 caracteres (findingAssignBody). */
export const INSTRUCCIONES_MAX = 500;

/** Recorta en límite de palabra para que el prellenado quepa en /assign. */
export function recortarInstrucciones(text: string, max = INSTRUCCIONES_MAX): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const corte = t.slice(0, max);
  const espacio = corte.lastIndexOf(" ");
  return (espacio > max * 0.6 ? corte.slice(0, espacio) : corte).trimEnd();
}

/** La frase de fase que acompaña al estado en la fila «Hito». */
function hitoFrase(state: PresentationalState, ctx: GestionContext): string | null {
  const { kind } = state;
  switch (kind) {
    case "open":
      return "por aceptar";
    case "reopened":
      return "por reasignar";
    case "accepted":
      return "por asignar";
    case "assigned":
      return "en reparación";
    case "resolved":
      // «a» + «el …» se contrae: «espera al propietario».
      return ctx.verdict.conforme
        ? "por revisar"
        : `espera ${ctx.words.closers.replace(/^el /, "al ")}`;
    case "rejected":
      // ADR-0141: el rechazo fue comunicado; responde el propietario. El
      // rechazo TERMINAL de un grafo sin carril de espera (facility:
      // anulada, fase closed) no espera a nadie — sin frase, como antes.
      return state.phase === "closed"
        ? null
        : `espera ${ctx.words.elAssetParty.replace(/^el /, "al ")}`;
    case "appealed":
      return "segunda revisión por resolver";
    default:
      return null;
  }
}

/** El aviso cuando el correo al asset_party no salió. Con ciclo de vida
 *  del asset (postventa) la causa es que no lo activó; sin él (facility),
 *  que no hay nadie con cuenta en ese carril. */
function notNotified(w: ThreadWords): string {
  return w.assetActivation
    ? `${capitalize(w.elAssetParty)} no tiene la ${w.assetLower} activada; no se le envió correo.`
    : `La ${w.assetLower} no tiene ${w.elAssetParty.replace(/^el /, "")} con cuenta; no se le envió correo.`;
}

/** ADR-0147 (corte de ajustes) — el propietario apagó este correo en sus
 *  preferencias: no es un fallo, pero tampoco «se notificó». */
function eligioNoRecibir(w: ThreadWords): string {
  return `${capitalize(w.elAssetParty)} eligió no recibir este aviso por correo.`;
}

/**
 * El toast de un acto que le escribe al asset_party: «· se notificó al
 * propietario» sólo si el correo salió; con el correo apagado por él,
 * «El propietario eligió no recibir este aviso por correo»; si no salió,
 * el motivo. Lee `ownerNotice` y, de un core-api anterior que no lo
 * manda, `ownerNotified` (ausente = avisado, como antes).
 */
export function avisoAlAssetParty(
  hecho: string,
  res: { ownerNotified?: boolean | undefined; ownerNotice?: string | undefined },
  w: ThreadWords
): string {
  const notice = res.ownerNotice ?? (res.ownerNotified === false ? "failed" : "sent");
  if (notice === "suppressed_by_preference") return `${hecho}. ${eligioNoRecibir(w)}`;
  if (notice === "failed") return `${hecho}. ${notNotified(w)}`;
  return `${hecho} · se notificó al ${w.assetParty.toLowerCase()}`;
}

export function GestionPanel({
  observation: o,
  state,
  actions,
  appeal,
  propietario,
  ctx,
  reabiertaTrasCierre = false,
  visita = SIN_VISITA,
}: {
  observation: Obs;
  state: PresentationalState;
  actions: GestionActions;
  /** ADR-0141 — la resolución de la apelación para este carril
   *  (`resolveAppealActions`); sin destinos no hay botones. */
  appeal: AppealActions;
  /** Nombre del asset_party (el propietario), o null si no lo hay. */
  propietario: string | null;
  ctx: GestionContext;
  /** ADR-0142 §4 — el último ciclo lo abrió la reapertura de un cierre
   *  por falta de respuesta (no un «No conforme»). Sale del hilo. */
  reabiertaTrasCierre?: boolean;
  /** F8 — la visita (sin ella, ni campos ni acción: facility). */
  visita?: VisitaPanel;
}) {
  const [emitted, setEmitted] = useState<{ url: string; revoked: boolean } | null>(null);
  const w = ctx.words;
  const sinAsignado =
    state.kind === "open" || state.kind === "reopened" || state.kind === "accepted";
  const asignado = sinAsignado
    ? "Sin asignar en este ciclo"
    : (o.provider?.shortName ?? "Sin asignar");
  const frase = hitoFrase(state, ctx);
  const propuesta = visitaPropuestaRows(visita.proposal, visita.history, o.visit);

  return (
    <section className="panel" aria-labelledby="obs-gestion">
      <div className="drawer__section-label" id="obs-gestion">
        Gestión
      </div>
      <div className="info-row">
        <span className="info-row__k">Hito</span>
        <span className="info-row__v">{frase ? `${state.label} · ${frase}` : state.label}</span>
      </div>
      <div className="info-row">
        <span className="info-row__k">Ciclo</span>
        <span className="info-row__v">
          {state.reopenCount > 0
            ? w.singleAssetPartyCloser
              ? `${state.ciclo} · reabierta por ${w.elAssetParty}`
              : `${state.ciclo} · reabierta por no conformidad`
            : String(state.ciclo)}
        </span>
      </div>
      <div className="info-row">
        <span className="info-row__k">{w.assignee}</span>
        <span className="info-row__v">{asignado}</span>
      </div>
      {/* F8 — la visita agendada y el pedido de nueva visita pendiente,
          mientras la observación está con el contratista. */}
      {state.kind === "assigned" && o.visit && (
        <div className="info-row">
          <span className="info-row__k">Visita</span>
          <span className="info-row__v">{visitaLabel(o.visit)}</span>
        </div>
      )}
      {/* ADR-0160 — la propuso el contratista desde su enlace. */}
      {state.kind === "assigned" && o.visit && propuesta.agendo && (
        <div className="info-row">
          <span className="info-row__k">Agendó</span>
          <span className="info-row__v">{propuesta.agendo}</span>
        </div>
      )}
      {/* ADR-0161 — la respuesta del propietario (o si se confirmará sola). */}
      {state.kind === "assigned" && o.visit && propuesta.confirmacion && (
        <div className="info-row">
          <span className="info-row__k">Confirmación</span>
          <span className="info-row__v">{propuesta.confirmacion}</span>
        </div>
      )}
      {state.kind === "assigned" && o.visit && propuesta.despuesDelPlazo && (
        <p className="notice-banner">{propuesta.despuesDelPlazo}</p>
      )}
      {state.kind === "assigned" && propuesta.otroHorario && (
        <p className="notice-banner">
          {`El propietario pidió otro horario: ${propuesta.otroHorario.texto}. `}
          {propuesta.otroHorario.contratistaPuede
            ? "El contratista puede proponer otra desde su enlace, o cámbiala tú."
            : "El contratista ya no puede proponer otra: coordínala desde el panel."}
        </p>
      )}
      {state.kind === "assigned" && propuesta.avisoNoEnviado && (
        <p className="notice-banner">{propuesta.avisoNoEnviado}</p>
      )}
      {state.kind === "assigned" && propuesta.antes.length > 0 && (
        <div className="info-row">
          <span className="info-row__k">Antes</span>
          <span className="info-row__v">{propuesta.antes.join(" · ")}</span>
        </div>
      )}
      {/* ADR-0150 — con quién se coordina el acceso, en toda fase: el jefe
          lo necesita al asignar, al agendar y al reagendar. */}
      {visita.enabled && visita.contact && (
        <div className="info-row">
          <span className="info-row__k">Acceso</span>
          <span className="info-row__v">{visitContactCoordinationLine(visita.contact)}</span>
        </div>
      )}
      {state.kind === "assigned" && visita.request && (
        <div className="info-row">
          <span className="info-row__k">Visita solicitada</span>
          <span className="info-row__v">
            {visitReasonLabelForAsset(visita.request.reason, w.asset)}
            {visita.request.note ? ` · «${visita.request.note}»` : ""}
          </span>
        </div>
      )}
      {/* ADR-0142 — las fechas las proyecta el servidor; sin ellas (job
          apagado, ventana vencida) la fila no existe. */}
      {state.kind === "resolved" && o.autoApprovalAt && (
        <div className="info-row">
          <span className="info-row__k">Cierre previsto</span>
          <span className="info-row__v">{fechaApoyoPropietario(o.autoApprovalAt)}</span>
        </div>
      )}
      {state.kind === "closed_no_response" && o.reopenableUntil && (
        <div className="info-row">
          <span className="info-row__k">Puede reabrirse hasta</span>
          <span className="info-row__v">{fechaApoyoPropietario(o.reopenableUntil)}</span>
        </div>
      )}
      <div className="g-body">
        {emitted ? (
          <EnlaceEmitido emitted={emitted} words={w} onDone={() => setEmitted(null)} />
        ) : (
          <GestionBody
            // Cada fase arranca su cuerpo limpio (rechazando, reasignando,
            // textos): una transición exitosa no arrastra el estado local.
            key={state.kind}
            observation={o}
            state={state}
            actions={actions}
            appeal={appeal}
            propietario={propietario}
            ctx={ctx}
            reabiertaTrasCierre={reabiertaTrasCierre}
            visita={visita}
            onAssigned={setEmitted}
          />
        )}
      </div>
    </section>
  );
}

function EnlaceEmitido({
  emitted,
  words: w,
  onDone,
}: {
  emitted: { url: string; revoked: boolean };
  words: ThreadWords;
  onDone: () => void;
}) {
  return (
    <>
      <p className="g-text">
        {emitted.revoked ? `${w.assignee} reasignado.` : "Observación asignada."} Mándale este
        enlace por WhatsApp o como prefieras. No necesita cuenta: verá sólo lo que le toca ejecutar
        en esta {w.assetLower}.
      </p>
      <CopyLinkField
        id="obs-assign-url"
        label={`Enlace del ${w.assigneeLower}`}
        value={emitted.url}
      />
      {emitted.revoked && (
        <p className="g-text g-text--meta">
          El enlace del {w.assigneeLower} anterior quedó revocado: deja de ver la observación, su
          evidencia y la dirección de la {w.assetLower}.
        </p>
      )}
      <div className="g-actions">
        <button type="button" className="btn btn--primary" onClick={onDone}>
          Listo
        </button>
      </div>
    </>
  );
}

function CorregirClasificacion() {
  // Destino pendiente: no hay pantalla todavía (README §Fuera de alcance).
  return (
    <div className="g-foot">
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled
        aria-disabled="true"
        title="Próximamente"
      >
        Corregir clasificación
      </button>
    </div>
  );
}

function GestionBody({
  observation: o,
  state,
  actions,
  appeal,
  propietario,
  ctx,
  reabiertaTrasCierre,
  visita,
  onAssigned,
}: {
  observation: Obs;
  state: PresentationalState;
  actions: GestionActions;
  appeal: AppealActions;
  propietario: string | null;
  ctx: GestionContext;
  reabiertaTrasCierre: boolean;
  visita: VisitaPanel;
  onAssigned: (e: { url: string; revoked: boolean }) => void;
}) {
  const [rechazando, setRechazando] = useState(false);
  const [reasignando, setReasignando] = useState(false);
  const [agendando, setAgendando] = useState(false);
  const { acceptTarget, rejectTarget, assignTarget, assignReachable } = actions;
  const w = ctx.words;
  const quien = propietario ?? capitalize(w.elAssetParty);
  const foot =
    ctx.flavor === "classify_and_assign" &&
    (state.kind === "open" || state.kind === "reopened" || state.kind === "accepted") ? (
      <CorregirClasificacion />
    ) : null;
  const rechazo = rejectTarget ? rejectWords(rejectTarget) : null;

  const rechazoForm = (target: GestionTarget) => (
    <>
      <RechazoForm
        findingId={o.id}
        target={target}
        words={w}
        onCancel={() => setRechazando(false)}
      />
      {foot}
    </>
  );

  // Asignar según el sabor de la receta. `onReject` pinta el cierre sin
  // trabajo junto al botón principal (reabierta de postventa); el resto lo
  // pinta abajo, en el pie.
  const asignar = (opts: {
    reasignacion?: boolean;
    onReject?: () => void;
    onCancel?: () => void;
    onDone?: () => void;
  }) => {
    const done = (e: { url: string; revoked: boolean }) => {
      opts.onDone?.();
      onAssigned(e);
    };
    if (ctx.flavor === "pick_and_confirm") {
      return (
        <ProviderAssignForm
          observation={{
            id: o.id,
            severity: o.severity,
            effectiveSeverity: o.effectiveSeverity ?? null,
            providerId: o.providerId,
            assetTitle: ctx.assetTitle,
          }}
          notifiesAssetParty={assignTarget?.notifiesAssetParty === true}
          onAssigned={(r) => done({ url: r.url, revoked: r.revoked })}
          {...(opts.onCancel ? { onCancel: opts.onCancel } : {})}
        />
      );
    }
    return (
      <AsignarForm
        observation={o}
        reopenCount={state.reopenCount}
        words={w}
        onAssigned={done}
        visitas={visita.enabled}
        visitContact={visita.contact ?? null}
        {...(opts.reasignacion ? { reasignacion: true } : {})}
        {...(opts.onReject ? { onReject: opts.onReject, rejectVerb: rechazo?.verb ?? "" } : {})}
        {...(opts.onCancel ? { onCancel: opts.onCancel } : {})}
      />
    );
  };

  // El cierre sin trabajo como pie, para el sabor de facility: el formulario
  // de asignar tiene su propio botón principal.
  const rejectFoot =
    rejectTarget && rechazo ? (
      <div className="g-foot">
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => setRechazando(true)}
        >
          {rechazo.verb}
        </button>
      </div>
    ) : null;

  if (rechazando && rejectTarget) return rechazoForm(rejectTarget);

  if (state.kind === "reopened" && (assignReachable || rejectTarget)) {
    const reabrio = reabiertaTrasCierre
      ? `${quien} reabrió la observación tras el cierre por falta de respuesta.`
      : w.singleAssetPartyCloser
        ? `${quien} marcó no conforme.`
        : "Se marcó no conforme.";
    if (!assignReachable) {
      // Puede rechazar, pero la reasignación es de otro carril.
      return (
        <>
          <p className="g-text">
            {reabrio} La reasignación la hace {w.assigningParty}.
          </p>
          <div className="g-actions">
            <button type="button" className="btn btn--danger" onClick={() => setRechazando(true)}>
              {rechazo?.verb}
            </button>
          </div>
          {foot}
        </>
      );
    }
    return (
      <>
        <p className="g-text">
          {reabrio} Vuelve a asignarla, al mismo {w.assigneeLower} o a otro.
        </p>
        {ctx.flavor === "pick_and_confirm"
          ? asignar({})
          : asignar({
              reasignacion: true,
              ...(rejectTarget ? { onReject: () => setRechazando(true) } : {}),
            })}
        {ctx.flavor === "pick_and_confirm" ? rejectFoot : null}
        {foot}
      </>
    );
  }

  if (state.kind === "open" && (acceptTarget || rejectTarget)) {
    return (
      <>
        <AceptarForm
          findingId={o.id}
          quien={quien}
          words={w}
          acceptKey={acceptTarget?.key ?? null}
          rejectVerb={rechazo?.verb ?? null}
          onReject={() => setRechazando(true)}
        />
        {foot}
      </>
    );
  }

  if (state.kind === "accepted" && (assignTarget || rejectTarget)) {
    return (
      <>
        {assignTarget ? asignar({}) : null}
        {rejectFoot}
        {foot}
      </>
    );
  }

  if (state.kind === "assigned") {
    if (reasignando && assignTarget) {
      return asignar({
        onDone: () => setReasignando(false),
        onCancel: () => setReasignando(false),
      });
    }
    if (agendando && visita.schedulable) {
      return (
        <VisitaForm
          observation={o}
          words={w}
          visitContact={visita.contact ?? null}
          proposal={visita.proposal ?? null}
          onDone={() => setAgendando(false)}
          onCancel={() => setAgendando(false)}
        />
      );
    }
    const nombre = o.provider?.shortName ?? `el ${w.assigneeLower}`;
    return (
      <>
        <p className="notice-banner">
          En reparación con {nombre}. Pasa a «{ctx.resolvedLabel}» cuando{" "}
          {ctx.onlyAssigneeResolves ? `el ${w.assigneeLower}` : "se"} suba la evidencia.
        </p>
        {visita.schedulable && (
          <div className="g-actions">
            <button type="button" className="btn btn--primary" onClick={() => setAgendando(true)}>
              {o.visit ? "Cambiar visita" : "Agendar visita"}
            </button>
          </div>
        )}
        {(assignTarget || rejectTarget) && (
          <div className="g-actions">
            {assignTarget && (
              <button type="button" className="btn btn--ghost" onClick={() => setReasignando(true)}>
                Reasignar
              </button>
            )}
            {rejectTarget && rechazo && (
              <button type="button" className="btn btn--danger" onClick={() => setRechazando(true)}>
                {rechazo.verb}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  if (state.kind === "resolved") {
    if (ctx.verdict.conforme) {
      return <VeredictoForm findingId={o.id} ciclo={state.ciclo} ctx={ctx} />;
    }
    const marco = ctx.onlyAssigneeResolves
      ? `El ${w.assigneeLower} marcó el trabajo como resuelto.`
      : "El trabajo se marcó como resuelto.";
    return (
      <p className="g-text">
        {o.autoApprovalAt
          ? `${marco} Ahora decide ${w.closers}. ${cierrePrevistoPersonal(o.autoApprovalAt)}; no hay acción pendiente de tu lado.`
          : `${marco} Ahora decide ${w.closers}: no se cierra sola y no hay acción pendiente de tu lado.`}
      </p>
    );
  }

  // ADR-0141 — el rechazo comunicado espera al propietario: puede
  // aceptarlo o pedir una segunda revisión. Nada que hacer de este lado.
  // El rechazo TERMINAL (facility: anulada, fase closed) no espera a
  // nadie: cae al cuerpo de cerrada de más abajo.
  if (state.kind === "rejected" && state.phase !== "closed") {
    return (
      <p className="g-text">
        El rechazo fue comunicado. {capitalize(ctx.words.elAssetParty)} puede aceptarlo o pedir una
        segunda revisión; no hay acción pendiente de tu lado.
      </p>
    );
  }

  // ADR-0141 — la apelación: acoger (vuelve al cauce) o mantener el
  // rechazo (motivo obligatorio, se le envía al propietario). Los
  // destinos salen del grafo (`resolveAppealActions`); sin destino para
  // este carril no hay botón.
  if (state.kind === "appealed") {
    if (appeal.acogerTarget || appeal.mantenerTarget) {
      return <ApelacionForm findingId={o.id} quien={quien} appeal={appeal} words={w} />;
    }
    return (
      <p className="g-text">
        {quien} pidió una segunda revisión. La resuelve {w.assigningParty}.
      </p>
    );
  }

  if (state.kind === "closed_no_response") {
    // ADR-0142 §3 — nunca una conformidad: se cerró sin respuesta.
    return (
      <p className="g-text">
        Cerrada por falta de respuesta: {quien} no confirmó a tiempo.
        {o.reopenableUntil
          ? ` Puede reabrirla hasta el ${fechaApoyoPropietario(o.reopenableUntil)}.`
          : ""}{" "}
        Queda en el archivo{ctx.archivoDe} con su hilo completo.
      </p>
    );
  }

  if (
    state.kind === "conforme" ||
    state.kind === "rejected_closed" ||
    // El rechazo terminal de facility (anulada): mismo cuerpo de cerrada
    // que antes de ADR-0141.
    state.kind === "rejected"
  ) {
    return (
      <p className="g-text">
        Observación cerrada. Queda en el archivo{ctx.archivoDe} con su hilo completo.
      </p>
    );
  }

  // Pendiente, pero no para mi carril (un admin frente a la asignación).
  return (
    <>
      <p className="g-text">
        El siguiente paso corresponde a otro rol: la asignación la hace {w.assigningParty}.
      </p>
      {foot}
    </>
  );
}

/** ADR-0141 — resolver la apelación: acoger (la observación vuelve a
 *  estar aceptada) o mantener el rechazo con motivo obligatorio, que se
 *  le envía al propietario. Ambas van por /observation-status: la
 *  resolución es del jefe (assigning_party), no un veredicto del carril
 *  de cierre. El motivo de la apelación se lee en el hilo. */
function ApelacionForm({
  findingId,
  quien,
  appeal,
  words: w,
}: {
  findingId: string;
  quien: string;
  appeal: AppealActions;
  words: ThreadWords;
}) {
  const [manteniendo, setManteniendo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const transition = useUpdateObservationStatus();
  const toast = useToast();
  const fieldId = useId();
  const hintId = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const pending = transition.isPending;
  const motivoReady = motivo.trim().length > 0 && !pending;

  useEffect(() => {
    if (manteniendo) ref.current?.focus();
  }, [manteniendo]);

  function acoger() {
    if (pending || !appeal.acogerTarget) return;
    transition.mutate(
      { id: findingId, body: { status: appeal.acogerTarget.key } },
      {
        onSuccess: (res) => toast.show(avisoAlAssetParty("Apelación acogida", res, w)),
      }
    );
  }

  function mantener() {
    if (!motivoReady || !appeal.mantenerTarget) return;
    transition.mutate(
      { id: findingId, body: { status: appeal.mantenerTarget.key, note: motivo.trim() } },
      {
        onSuccess: (res) => toast.show(avisoAlAssetParty("Rechazo mantenido", res, w)),
      }
    );
  }

  const error = transition.isError && (
    <p className="auth-error" role="alert">
      No pudimos registrar la resolución: el estado puede haber cambiado. Recarga e intenta de
      nuevo.
    </p>
  );

  if (manteniendo && appeal.mantenerTarget) {
    return (
      <>
        <div className="field">
          <label className="field__label" htmlFor={fieldId}>
            Motivo (se le envía al {w.assetParty.toLowerCase()})
          </label>
          <textarea
            id={fieldId}
            ref={ref}
            className="textarea"
            rows={3}
            required
            aria-required="true"
            aria-describedby={hintId}
            maxLength={4000}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <span className="field__hint" id={hintId}>
            {capitalize(w.elAssetParty)} verá este motivo. La observación queda cerrada y no admite
            otra apelación.
          </span>
        </div>
        <div className="g-actions">
          <button
            type="button"
            className="btn btn--danger"
            aria-disabled={!motivoReady}
            aria-describedby={hintId}
            onClick={mantener}
          >
            {pending ? "Enviando…" : "Confirmar rechazo"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setManteniendo(false)}
            disabled={pending}
          >
            Cancelar
          </button>
        </div>
        {error}
      </>
    );
  }

  return (
    <>
      <p className="g-text">
        {quien} pidió una segunda revisión del rechazo. Su motivo está en el hilo. Si la acoges, la
        observación vuelve a estar aceptada; si mantienes el rechazo, indica el motivo.
      </p>
      <div className="g-actions">
        {appeal.acogerTarget && (
          <button
            type="button"
            className="btn btn--primary"
            aria-disabled={pending}
            onClick={acoger}
          >
            {pending ? "Enviando…" : "Acoger apelación"}
          </button>
        )}
        {appeal.mantenerTarget && (
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => setManteniendo(true)}
            disabled={pending}
          >
            Mantener rechazo
          </button>
        )}
      </div>
      {error}
    </>
  );
}

/** «Rechazar» → «rechazas», «Anular» → «anulas»: los verbos de cierre sin
 *  trabajo son todos regulares en -ar. */
function segundaPersona(verbo: string): string {
  return verbo.toLowerCase().replace(/ar$/, "as");
}

function AceptarForm({
  findingId,
  quien,
  words: w,
  acceptKey,
  rejectVerb,
  onReject,
}: {
  findingId: string;
  quien: string;
  words: ThreadWords;
  acceptKey: string | null;
  /** Verbo del cierre sin trabajo, o null si el carril no lo tiene. */
  rejectVerb: string | null;
  onReject: () => void;
}) {
  // Aceptar no lleva mensaje (fundador 2026-10-11): el servidor avisa al
  // propietario que su observación quedó en espera de asignación.
  const transition = useUpdateObservationStatus();
  const toast = useToast();
  const ready = acceptKey !== null && !transition.isPending;

  function aceptar() {
    if (!ready || acceptKey === null) return;
    transition.mutate(
      { id: findingId, body: { status: acceptKey } },
      {
        onSuccess: (res) => toast.show(avisoAlAssetParty("Observación aceptada", res, w)),
      }
    );
  }

  return (
    <>
      <p className="g-text">
        {quien} espera tu respuesta. Si aceptas, le avisaremos que su observación quedó en espera de
        asignación{rejectVerb ? `; si ${segundaPersona(rejectVerb)}, indica el motivo` : ""}.
      </p>
      <div className="g-actions">
        {acceptKey !== null && (
          <button
            type="button"
            className="btn btn--primary"
            aria-disabled={!ready}
            onClick={aceptar}
          >
            {transition.isPending ? "Enviando…" : "Aceptar"}
          </button>
        )}
        {rejectVerb && (
          <button
            type="button"
            className="btn btn--danger"
            onClick={onReject}
            disabled={transition.isPending}
          >
            {rejectVerb}
          </button>
        )}
      </div>
      {transition.isError && (
        <p className="auth-error" role="alert">
          No pudimos aceptar la observación: el estado puede haber cambiado. Recarga e intenta de
          nuevo.
        </p>
      )}
    </>
  );
}

/** Rechazar (postventa) o Anular (facility): el destino terminal sin
 *  aristas, con motivo obligatorio — el engine lo exige (`requiresNote`)
 *  y sin él el asset_party y el asignado reciben un cierre sin
 *  explicación. */
function RechazoForm({
  findingId,
  target,
  words: w,
  onCancel,
}: {
  findingId: string;
  target: GestionTarget;
  words: ThreadWords;
  onCancel: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const transition = useUpdateObservationStatus();
  const toast = useToast();
  const fieldId = useId();
  const hintId = useId();
  const ready = motivo.trim().length > 0 && !transition.isPending;
  const ref = useRef<HTMLTextAreaElement>(null);
  const rw = rejectWords(target);
  // El bloque aparece por un clic en «Rechazar» / «Anular»: el foco va al
  // campo que hay que llenar.
  useEffect(() => ref.current?.focus(), []);

  function confirmar() {
    if (!ready) return;
    transition.mutate(
      { id: findingId, body: { status: target.key, note: motivo.trim() } },
      {
        onSuccess: (res) => {
          const hecho = `Observación ${rw.participle}`;
          // Sin «se notificó» cuando salió (copy de siempre); sí el motivo
          // cuando no salió o el propietario apagó el correo.
          const notice = res.ownerNotice ?? (res.ownerNotified === false ? "failed" : "sent");
          toast.show(notice === "sent" ? hecho : avisoAlAssetParty(hecho, res, w));
        },
      }
    );
  }

  return (
    <>
      <p className="g-text">
        Una observación {rw.participle} no se reabre: si {w.elAssetParty} insiste, levanta una
        nueva.
      </p>
      <div className="field">
        <label className="field__label" htmlFor={fieldId}>
          {rw.reasonLabel}
        </label>
        <textarea
          id={fieldId}
          ref={ref}
          className="textarea"
          rows={3}
          required
          aria-required="true"
          aria-describedby={hintId}
          placeholder={`Se muestra al ${w.assetParty.toLowerCase()} en el hilo`}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <span className="field__hint" id={hintId}>
          {capitalize(w.elAssetParty)} verá este motivo. La observación queda cerrada.
        </span>
      </div>
      <div className="g-actions">
        <button
          type="button"
          className="btn btn--primary"
          aria-disabled={!ready}
          aria-describedby={hintId}
          onClick={confirmar}
        >
          {transition.isPending ? "Enviando…" : rw.confirm}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onCancel}
          disabled={transition.isPending}
        >
          Cancelar
        </button>
      </div>
      {transition.isError && (
        <p className="auth-error" role="alert">
          No pudimos registrar el cambio. Intenta de nuevo.
        </p>
      )}
    </>
  );
}

/** Conforme / No conforme sobre lo resuelto (POST /findings/:id/conformity).
 *  Conforme pide una segunda confirmación —cierra la observación—; No
 *  conforme pide el motivo y reabre con un ciclo nuevo. */
function VeredictoForm({
  findingId,
  ciclo,
  ctx,
}: {
  findingId: string;
  ciclo: number;
  ctx: GestionContext;
}) {
  const [mode, setMode] = useState<"choose" | "conforme" | "no_conforme">("choose");
  const [motivo, setMotivo] = useState("");
  const conformity = useConformity();
  const toast = useToast();
  const fieldId = useId();
  const hintId = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const w = ctx.words;
  const pending = conformity.isPending;
  const motivoReady = motivo.trim().length > 0 && !pending;

  useEffect(() => {
    if (mode === "no_conforme") ref.current?.focus();
  }, [mode]);

  function darConforme() {
    if (pending) return;
    conformity.mutate(
      { id: findingId, body: { verdict: "conforme" } },
      { onSuccess: () => toast.show("Observación cerrada") }
    );
  }

  function darNoConforme() {
    if (!motivoReady) return;
    conformity.mutate(
      { id: findingId, body: { verdict: "no_conforme", note: motivo.trim() } },
      {
        onSuccess: () =>
          toast.show(`Observación reabierta · ciclo ${ciclo + 1} · vuelve a «${ctx.entryLabel}»`),
      }
    );
  }

  const error = conformity.isError && (
    <p className="auth-error" role="alert">
      No pudimos registrar tu revisión: el estado puede haber cambiado. Recarga e intenta de nuevo.
    </p>
  );

  if (mode === "conforme") {
    return (
      <>
        <p className="g-text">
          Al dar conformidad, la observación queda cerrada con su hilo completo.
        </p>
        <div className="g-actions">
          <button
            type="button"
            className="btn btn--primary"
            aria-disabled={pending}
            onClick={darConforme}
          >
            {pending ? "Enviando…" : "Confirmar conformidad"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setMode("choose")}
            disabled={pending}
          >
            Cancelar
          </button>
        </div>
        {error}
      </>
    );
  }

  if (mode === "no_conforme") {
    return (
      <>
        <div className="field">
          <label className="field__label" htmlFor={fieldId}>
            Qué sigue mal
          </label>
          <textarea
            id={fieldId}
            ref={ref}
            className="textarea"
            rows={3}
            required
            aria-required="true"
            aria-describedby={hintId}
            maxLength={4000}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Escape") setMode("choose");
            }}
          />
          <span className="field__hint" id={hintId}>
            El motivo es obligatorio: es la instrucción que recibe quien vuelva a trabajarla. La
            observación vuelve a «{ctx.entryLabel}» y abre el ciclo {ciclo + 1}.
          </span>
        </div>
        <div className="g-actions">
          <button
            type="button"
            className="btn btn--danger"
            aria-disabled={!motivoReady}
            aria-describedby={hintId}
            onClick={darNoConforme}
          >
            {pending ? "Enviando…" : "Confirmar y reabrir"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setMode("choose")}
            disabled={pending}
          >
            Cancelar
          </button>
        </div>
        {error}
      </>
    );
  }

  return (
    <>
      <p className="g-text">
        {ctx.onlyAssigneeResolves
          ? `El ${w.assigneeLower} marcó el trabajo como resuelto.`
          : "El trabajo se marcó como resuelto."}{" "}
        Revisa la evidencia del hilo y da tu conformidad, o márcala no conforme para que se vuelva a
        trabajar.
      </p>
      <div className="g-actions">
        <button type="button" className="btn btn--primary" onClick={() => setMode("conforme")}>
          {VEREDICTO_LABEL.conforme}
        </button>
        {ctx.verdict.noConforme && (
          <button type="button" className="btn btn--danger" onClick={() => setMode("no_conforme")}>
            {VEREDICTO_LABEL.no_conforme}
          </button>
        )}
      </div>
    </>
  );
}

function AsignarForm({
  observation: o,
  reopenCount,
  words: w,
  onAssigned,
  onCancel,
  reasignacion = false,
  onReject,
  rejectVerb,
  visitas = false,
  visitContact = null,
}: {
  observation: Obs;
  reopenCount: number;
  words: ThreadWords;
  onAssigned: (e: { url: string; revoked: boolean }) => void;
  /** F8 — la receta agenda visitas: el formulario ofrece el día y el
   *  horario, opcionales. */
  visitas?: boolean;
  /** ADR-0150 — con quién coordinar el acceso (arrendataria). */
  visitContact?: UnitVisitContact | null;
  onCancel?: () => void;
  /** Reabierta: el botón dice «Reasignar» y `/assign` acepta de paso. */
  reasignacion?: boolean;
  /** El «Rechazar» de una reabierta, junto al botón principal. */
  onReject?: () => void;
  rejectVerb?: string;
}) {
  const flow = useAssignFlow({
    findingId: o.id,
    taxonomyVersion: o.taxonomyVersion,
    failureTypeCode: o.failureTypeCode,
    currentSpecialtyCode: o.specialtyCode,
    currentItemCode: o.itemCode,
    aiSuggestedSpecialtyCode: o.aiSuggestedSpecialtyCode,
    aiSuggestedItemCode: o.aiSuggestedItemCode,
  });
  const toast = useToast();
  const sugerencia = o.aiRecommendation ? recortarInstrucciones(o.aiRecommendation) : "";
  const [note, setNote] = useState(sugerencia);
  const [providerId, setProviderId] = useState("");
  const [visitaDraft, setVisitaDraft] = useState<VisitaDraft>(VISITA_VACIA);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listLabelId = useId();
  const noteId = useId();
  const hintId = useId();
  const specialtyId = useId();
  const itemId = useId();

  const faltaVisita = visitas ? faltaDeVisita(visitaDraft, { optional: true }) : null;
  const ready =
    !flow.pending &&
    providerId !== "" &&
    note.trim().length > 0 &&
    flow.classificationReady &&
    faltaVisita === null;
  // Qué falta para poder asignar, visible junto al botón (el botón queda
  // atenuado con aria-disabled y apunta a este texto).
  const sinAsignado = providerId === "";
  const sinInstrucciones = note.trim().length === 0;
  const falta = flow.pending
    ? null
    : !flow.classificationReady
      ? "Elige la especialidad y la partida para poder asignar."
      : sinAsignado && sinInstrucciones
        ? `Elige un ${w.assigneeLower} y escribe las instrucciones para poder asignar.`
        : sinAsignado
          ? `Elige un ${w.assigneeLower} para poder asignar.`
          : sinInstrucciones
            ? `Escribe las instrucciones para el ${w.assigneeLower}.`
            : faltaVisita;
  const faltaId = useId();

  // Roving tabindex sobre los radios: Tab cae en el elegido (o el
  // primero); las flechas mueven selección y foco, como un radio nativo.
  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const nomina = flow.nomina;
    if (nomina.length === 0) return;
    const delta =
      e.key === "ArrowDown" || e.key === "ArrowRight"
        ? 1
        : e.key === "ArrowUp" || e.key === "ArrowLeft"
          ? -1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    const i = nomina.findIndex((p) => p.id === providerId);
    const next = nomina[(Math.max(i, 0) + delta + nomina.length) % nomina.length]!;
    setProviderId(next.id);
    listRef.current?.querySelector<HTMLElement>(`[data-provider-id="${next.id}"]`)?.focus();
  }

  async function asignar() {
    if (!ready) return;
    setError(null);
    try {
      const res = await flow.submit(providerId, note.trim(), {
        visit: visitas ? visitaBody(visitaDraft) : undefined,
      });
      const verbo = reasignacion ? "Reasignada" : "Asignada";
      toast.show(
        res.notified
          ? `${verbo} a ${res.quien} · enlace enviado`
          : `${verbo} a ${res.quien}. Copia el enlace y compártelo.`
      );
      onAssigned({ url: res.url, revoked: res.revoked });
    } catch (err) {
      setError(visitaErrorMessage(err, w, "No pudimos guardar la asignación"));
    }
  }

  return (
    <>
      {flow.needsClassification && (
        <>
          <p className="g-text">
            Falta la especialidad: la asignación la deja registrada junto a la sugerencia de la IA.
          </p>
          <div className="field">
            <label className="field__label" htmlFor={specialtyId}>
              Especialidad (quién repara)
            </label>
            <select
              id={specialtyId}
              className="select"
              value={flow.specialty}
              onChange={(e) => flow.setSpecialty(e.target.value)}
            >
              <option value="">Selecciona una especialidad…</option>
              {flow.especialidades.map((e) => (
                <option key={e.codigo} value={e.codigo}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor={itemId}>
              Partida (qué elemento)
            </label>
            <select
              id={itemId}
              className="select"
              value={flow.item}
              onChange={(e) => flow.setItem(e.target.value)}
              disabled={!flow.specialty}
            >
              <option value="">
                {flow.specialty ? "Selecciona una partida…" : "Elige primero la especialidad"}
              </option>
              {flow.partidas.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className="field">
        <div className="field__label" id={listLabelId}>
          Asignar a {w.assigneeLower}
        </div>
        <ProviderListBoundary query={flow.providers} className="prov-list g-list">
          {flow.providers.data && flow.providers.data.providers.length === 0 && (
            <p className="g-text">
              Todavía no hay {w.assigneePluralLower} en la nómina. Cárgalos primero desde{" "}
              {w.assigneePlural}.
            </p>
          )}
          <div
            ref={listRef}
            className="prov-list g-list"
            role="radiogroup"
            aria-labelledby={listLabelId}
            onKeyDown={onListKeyDown}
          >
            {flow.nomina.map((p, i) => {
              const checked = providerId === p.id;
              const focusable = checked || (providerId === "" && i === 0);
              const atendio = reopenCount > 0 && o.providerId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={focusable ? 0 : -1}
                  data-provider-id={p.id}
                  className={`prov-option${checked ? " prov-option--selected" : ""}`}
                  onClick={() => setProviderId(p.id)}
                >
                  <span className="prov-option__meta">
                    <span className="prov-option__name">{p.shortName}</span>
                    {atendio && (
                      <span className="prov-option__rec">Atendió el ciclo {reopenCount}</span>
                    )}
                    <span className="field__hint">
                      {[p.specialty, `${p.activeJobs} en curso`].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {checked && (
                    <span className="prov-option__check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </ProviderListBoundary>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={noteId}>
          Instrucciones
        </label>
        <textarea
          id={noteId}
          className="textarea"
          rows={4}
          required
          aria-required="true"
          aria-describedby={hintId}
          maxLength={INSTRUCCIONES_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={`Ej.: Revisar y reparar… Coordinar la visita con ${w.elAssetParty}.`}
        />
        <span className="field__hint" id={hintId}>
          {sugerencia
            ? reopenCount > 0
              ? "Sugeridas por la IA a partir del ciclo anterior."
              : "Sugeridas por la IA a partir de la evidencia."
            : `El ${w.assigneeLower} recibirá un enlace con la evidencia y tus instrucciones.`}
        </span>
      </div>

      {visitas && o.visit && (
        // Sólo una observación ya asignada tiene visita: esto es una
        // reasignación. Sin fecha nueva BORRA la vigente (es del
        // contratista que sale, ADR-0149 §2) y al propietario no le llega
        // aviso: el jefe lo sabe antes de confirmar.
        <p className="field__hint">
          {`Al reasignar se borra la visita del ${visitaLabel(o.visit)}. Si ya coordinaste otra con el nuevo ${w.assigneeLower}, agéndala aquí.`}
        </p>
      )}
      {visitas && visitContact && (
        <p className="field__hint">{visitContactCoordinationLine(visitContact)}</p>
      )}
      {visitas && (
        <VisitaFields
          value={visitaDraft}
          onChange={setVisitaDraft}
          optional
          elAssetParty={w.elAssetParty}
          disabled={flow.pending}
        />
      )}

      {falta && (
        <p className="field__hint" id={faltaId}>
          {falta}
        </p>
      )}
      <div className="g-actions">
        <button
          type="button"
          className="btn btn--primary"
          aria-disabled={!ready}
          aria-describedby={falta ? faltaId : undefined}
          onClick={() => void asignar()}
        >
          {flow.pending ? "Enviando…" : reasignacion ? "Reasignar" : "Asignar y enviar enlace"}
        </button>
        {onReject && (
          <button
            type="button"
            className="btn btn--danger"
            onClick={onReject}
            disabled={flow.pending}
          >
            {rejectVerb}
          </button>
        )}
        {onCancel && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onCancel}
            disabled={flow.pending}
          >
            Cancelar
          </button>
        )}
      </div>
      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

/**
 * F8 — «Agendar visita» / «Cambiar visita» mientras la observación está
 * con el contratista. Mismo día y horario que la vigente = el servidor no
 * avisa (`changed: false`) y el panel lo dice. Una clave de idempotencia
 * por intento: reintentar el MISMO envío no duplica el correo.
 */
function VisitaForm({
  observation: o,
  words: w,
  visitContact = null,
  proposal = null,
  onDone,
  onCancel,
}: {
  observation: Obs;
  words: ThreadWords;
  /** ADR-0150 — con quién coordinar el acceso (arrendataria). */
  visitContact?: UnitVisitContact | null;
  /** ADR-0160/0161 — la vigente la propuso el contratista: su estado y lo
   *  que pidió el propietario, si pidió otro horario. */
  proposal?: FindingDetailResponse["visitProposal"] | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const schedule = useScheduleVisit();
  const toast = useToast();
  const [draft, setDraft] = useState<VisitaDraft>(
    o.visit
      ? { date: o.visit.date, startTime: o.visit.startTime, endTime: o.visit.endTime }
      : VISITA_VACIA
  );
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<{ body: string; key: string } | null>(null);
  const faltaId = useId();
  const propuesta = visitaPropuestaRows(proposal, [], o.visit);
  const borrador = visitaBody(draft);
  // ADR-0161 — el horario que el propietario rechazó no se vuelve a
  // guardar; uno que no calza con lo que pidió se avisa sin bloquear.
  const rechazado =
    proposal?.confirmation === "other_time_requested" &&
    o.visit != null &&
    borrador !== undefined &&
    !visitChanged(o.visit, borrador);
  const noCalza =
    proposal?.confirmation === "other_time_requested" &&
    proposal.ownerPreference != null &&
    borrador !== undefined &&
    !rechazado &&
    !visitMatchesPreference(borrador, proposal.ownerPreference);
  const falta =
    faltaDeVisita(draft, { optional: false }) ??
    (rechazado ? VISIT_SAME_AS_DECLINED_MESSAGE : null);
  const ready = !schedule.isPending && falta === null;
  // «a» + «el …» se contrae: «al propietario».
  const alAssetParty = w.elAssetParty.replace(/^el /, "al ");

  async function guardar() {
    const body = visitaBody(draft);
    if (!ready || !body) return;
    setError(null);
    const sello = `${body.date}/${body.startTime}/${body.endTime}`;
    if (keyRef.current?.body !== sello) keyRef.current = { body: sello, key: crypto.randomUUID() };
    try {
      const res = await schedule.mutateAsync({
        id: o.id,
        body,
        idempotencyKey: keyRef.current.key,
      });
      const notice = res.ownerNotice ?? (res.ownerNotified ? "sent" : "failed");
      toast.show(
        !res.changed
          ? "La visita ya estaba agendada para ese día y horario."
          : notice === "sent"
            ? `Visita agendada: ${visitaLabel(res.visit)} · se le avisó ${alAssetParty}`
            : notice === "suppressed_by_preference"
              ? `Visita agendada: ${visitaLabel(res.visit)}. ${eligioNoRecibir(w)}`
              : // `failed` también es «nadie a quien escribirle» (unidad
                // sin activar): el texto no sugiere un fallo.
                `Visita agendada: ${visitaLabel(res.visit)}. No se le envió correo ${alAssetParty}.`
      );
      onDone();
    } catch (err) {
      setError(visitaErrorMessage(err, w, "No pudimos guardar la visita"));
    }
  }

  return (
    <>
      <p className="g-text">
        {o.visit
          ? `Visita agendada para el ${visitaLabel(o.visit)}. Elige el nuevo día y horario.`
          : `Elige el día y el horario en que el ${w.assigneeLower} irá a la ${w.assetLower}.`}
      </p>
      {visitContact && <p className="field__hint">{visitContactCoordinationLine(visitContact)}</p>}
      {proposal && propuesta.hint && <p className="field__hint">{propuesta.hint}</p>}
      <VisitaFields
        value={draft}
        onChange={setDraft}
        optional={false}
        elAssetParty={w.elAssetParty}
        disabled={schedule.isPending}
      />
      {falta && (
        <p className="field__hint" id={faltaId}>
          {falta}
        </p>
      )}
      {noCalza && <p className="field__hint">{VISIT_PREFERENCE_MISMATCH_HINT}</p>}
      <div className="g-actions">
        <button
          type="button"
          className="btn btn--primary"
          aria-disabled={!ready}
          aria-describedby={falta ? faltaId : undefined}
          onClick={() => void guardar()}
        >
          {schedule.isPending ? "Guardando…" : o.visit ? "Cambiar visita" : "Agendar visita"}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onCancel}
          disabled={schedule.isPending}
        >
          Cancelar
        </button>
      </div>
      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
